import {
  Component,
  effect,
  inject,
  input,
  output,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import {
  MarketingPricingService,
  PricingCustomization,
  PricingOverride
} from 'src/shared/services/marketing-pricing.service';
import { ConciergeApiService, CreateConciergeItemData } from 'src/shared/services/concierge-api.service';
import { Util } from 'src/shared/util/util';
import { lastValueFrom } from 'rxjs';

type CancellationPolicy = 'flexible' | 'moderate' | 'limited' | 'firm';
type Weekday = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';
type StayLengthKey = 'min-nights' | 'max-nights' | 'advance-notice' | 'preparation' | 'availability';
type GuestFilterKey = 'verifiedId' | 'profilePhoto' | 'goodTrackRecord';
type PriceRangeKey = 'min' | 'max';
type PlatformKey = 'airbnb' | 'booking' | 'vrbo';
type PromotionKey =
  'weekly-stays'
  | 'monthly-stays'
  | 'last-minute-booking'
  | 'far-out-booking'
  | 'member-only-exclusive'
  | 'mobile-rate'
  | 'flash-campaign-deal'
  | 'booking-basic-deal'
  | 'genius-loyalty';

interface RevenueLeverState {
  instantBook: { airbnb: boolean; booking: boolean; vrbo: boolean };
  petFriendly: { airbnb: boolean; booking: boolean; vrbo: boolean };
  cancellationPolicy: { airbnb: CancellationPolicy; booking: CancellationPolicy; vrbo: CancellationPolicy };
  nonRefundable: { airbnb: boolean; booking: boolean; vrbo: boolean; bookingPercent: number; vrboPercent: number };
  checkInBlocked: Weekday[];
  checkOutBlocked: Weekday[];
  // Stay-length controls (universal, single value)
  minNights: number;
  maxNights: number;
  advanceNoticeDays: number;       // 0 = same day, 1, 2, 3, 7
  preparationNights: number;       // 0, 1, 2
  availabilityMonths: number;      // 3, 6, 9, 12, 24
  // Instant Book filters (Airbnb)
  guestFilters: { verifiedId: boolean; profilePhoto: boolean; goodTrackRecord: boolean };
  // Seasonal cancellation policies (VRBO)
  seasonalCancellation: boolean;
  // Check-in / Check-out times (hours of day)
  checkInTime: string;   // '15:00' or 'flexible'
  checkOutTime: string;  // '11:00'
}

interface PromotionControl {
  key: PromotionKey;
  name: string;
  status: string;
  note: string;
  platforms: PlatformKey[];
  active: boolean;
  discountPercent?: number;
  thresholdDays?: number;
  thresholdLabel?: string;
}

interface PromotionEdit {
  discountPercent?: number;
  thresholdDays?: number;
}

@Component({
  selector: 'pricing-customization',
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    @if (customization) {
      <tidy-card class="settings-card">
        <tidy-row class="extra-bottom-padding settings-card-header">
          <div class="settings-heading">
            <tidy-text [header]="'Booking settings'" />
            <tidy-text [helper]="'Review pricing and channel rules. Changes may take up to two hours to apply.'" />
          </div>
        </tidy-row>

        <div class="settings-table">
          <!-- Instant Book -->
          <div class="settings-row">
            <div class="row-main">
              <div class="col-name">
                <span class="name-text">Instant Book</span>
                <span class="name-suffix">· filters {{ guestFiltersCount }} of 3 on</span>
                @if (instantBookRecoShort) {
                  <button type="button" class="reco-pill" [class.action]="instantBookRecommendation?.action" (click)="toggleInfo('instant-book')">
                    <span class="reco-arrow">→</span>
                    <span>{{ instantBookRecoShort }}</span>
                  </button>
                }
              </div>
              <div class="col-state">
                <button type="button" role="switch" class="state-switch"
                        [class.on]="allOn(levers.instantBook)"
                        [attr.aria-checked]="allOn(levers.instantBook)"
                        (click)="onSwitchClick('instant-book')"
                        aria-label="Toggle Instant Book">
                  <div class="switch-track"><div class="switch-thumb"></div></div>
                </button>
              </div>
              <div class="col-action">
                <button type="button" class="details-btn" (click)="toggleInfo('instant-book')" [attr.aria-expanded]="expandedSettings.has('instant-book')" aria-label="Toggle details">
                  <span class="details-chevron" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            @if (expandedSettings.has('instant-book')) {
              <div class="row-details">
                @if (instantBookRecommendation) {
                  <div class="details-reco" [class.action]="instantBookRecommendation.action">
                    <span class="reco-label">Opportunity</span>
                    <span class="reco-text">{{ instantBookRecommendation.text }}</span>
                  </div>
                }
                <div class="sub-toggles">
                  <div class="sub-toggle-row">
                    <div class="sub-toggle-copy">
                      <div class="sub-toggle-name">Require verified government ID</div>
                    </div>
                    <button type="button" class="state-switch compact" [class.on]="levers.guestFilters.verifiedId" [class.off]="!levers.guestFilters.verifiedId" (click)="toggleGuestFilter('verifiedId')">
                      <div class="switch-track"><div class="switch-thumb"></div></div>
                    </button>
                  </div>
                  <div class="sub-toggle-row">
                    <div class="sub-toggle-copy">
                      <div class="sub-toggle-name">Require profile photo</div>
                    </div>
                    <button type="button" class="state-switch compact" [class.on]="levers.guestFilters.profilePhoto" [class.off]="!levers.guestFilters.profilePhoto" (click)="toggleGuestFilter('profilePhoto')">
                      <div class="switch-track"><div class="switch-thumb"></div></div>
                    </button>
                  </div>
                  <div class="sub-toggle-row">
                    <div class="sub-toggle-copy">
                      <div class="sub-toggle-name">Require good track record</div>
                      <div class="sub-toggle-note">No negative reviews or prior host issues.</div>
                    </div>
                    <button type="button" class="state-switch compact" [class.on]="levers.guestFilters.goodTrackRecord" [class.off]="!levers.guestFilters.goodTrackRecord" (click)="toggleGuestFilter('goodTrackRecord')">
                      <div class="switch-track"><div class="switch-thumb"></div></div>
                    </button>
                  </div>
                </div>
                <div class="lever-why">
                  Industry studies show un-optimized listings see +20-40% booking rate after turning Instant
                  Book on — sometimes much higher. Net revenue lift sits in the lower end because (1) extra
                  bookings skew toward last-minute fills, and (2) instant-book reservations cancel
                  <em>more</em> often than request-book. Pair Instant Book with the guest filters below to
                  keep guest quality up.
                  <strong>Risk:</strong> locks in your pricing — calibrate base/min/max first.
                </div>
              </div>
            }
          </div>

          <!-- Pet-Friendly -->
          <div class="settings-row">
            <div class="row-main">
              <div class="col-name">
                <span class="name-text">Pet-Friendly</span>
                @if (petFriendlyRecoShort) {
                  <button type="button" class="reco-pill" [class.action]="petFriendlyRecommendation?.action" (click)="toggleInfo('pet-friendly')">
                    <span class="reco-arrow">→</span>
                    <span>{{ petFriendlyRecoShort }}</span>
                  </button>
                }
              </div>
              <div class="col-state">
                <button type="button" role="switch" class="state-switch"
                        [class.on]="allOn(levers.petFriendly)"
                        [attr.aria-checked]="allOn(levers.petFriendly)"
                        (click)="onSwitchClick('pet-friendly')"
                        aria-label="Toggle Pet-Friendly">
                  <div class="switch-track"><div class="switch-thumb"></div></div>
                </button>
              </div>
              <div class="col-action">
                <button type="button" class="details-btn" (click)="toggleInfo('pet-friendly')" [attr.aria-expanded]="expandedSettings.has('pet-friendly')" aria-label="Toggle details">
                  <span class="details-chevron" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            @if (expandedSettings.has('pet-friendly')) {
              <div class="row-details">
                @if (petFriendlyRecommendation) {
                  <div class="details-reco" [class.action]="petFriendlyRecommendation.action">
                    <span class="reco-label">Opportunity</span>
                    <span class="reco-text">{{ petFriendlyRecommendation.text }}</span>
                  </div>
                }
                <div class="sub-toggles">
                  <div class="sub-toggle-row">
                    <div class="sub-toggle-copy">
                      <div class="sub-toggle-name">Pet fee</div>
                      <div class="sub-toggle-note">Fee charged when guests bring pets.</div>
                    </div>
                    <label class="money-input" aria-label="Pet fee amount">
                      <span>$</span>
                      <input type="number" min="0" step="5" [value]="petFeeAmount" (change)="setPetFeeAmount($any($event.target).value)" />
                    </label>
                  </div>
                </div>
                <div class="lever-why">
                  Pet-friendly listings get more search demand. Use a fee or deposit to cover cleaning and damage.
                  Skip this if pet damage would be expensive to repair.
                </div>
              </div>
            }
          </div>

          <!-- Cancellation Policy -->
          <div class="settings-row">
            <div class="row-main">
              <div class="col-name">
                <span class="name-text">Cancellation Policy</span>
                @if (cancellationRecoShort) {
                  <button type="button" class="reco-pill" [class.action]="cancellationRecommendation?.action" (click)="toggleInfo('cancellation')">
                    <span class="reco-arrow">→</span>
                    <span>{{ cancellationRecoShort }}</span>
                  </button>
                }
              </div>
              <div class="col-state">
                <button type="button" class="state-pill policy" (click)="toggleInfo('cancellation')" aria-label="Change cancellation policy">
                  <span>{{ cancellationPillLabel }}</span>
                  <span class="chevron">▾</span>
                </button>
              </div>
              <div class="col-action">
                <button type="button" class="details-btn" (click)="toggleInfo('cancellation')" [attr.aria-expanded]="expandedSettings.has('cancellation')" aria-label="Toggle details">
                  <span class="details-chevron" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            @if (expandedSettings.has('cancellation')) {
              <div class="row-details">
                @if (cancellationRecommendation) {
                  <div class="details-reco" [class.action]="cancellationRecommendation.action">
                    <span class="reco-label">Opportunity</span>
                    <span class="reco-text">{{ cancellationRecommendation.text }}</span>
                  </div>
                }
                <div class="value-picker">
                  @for (opt of cancellationOptions; track opt.value) {
                    <div class="picker-option"
                         [class.active]="levers.cancellationPolicy.airbnb === opt.value"
                         (click)="requestLeverChange('cancellation', opt.value)">
                      <div class="picker-name">
                        {{ opt.label }}
                      </div>
                      <div class="picker-subtitle">{{ opt.subtitle }}</div>
                      <div class="picker-lift" [class.up]="opt.liftMax > 0" [class.neutral]="opt.liftMax === 0">{{ opt.liftLabel }}</div>
                    </div>
                  }
                </div>
                <div class="lever-why">
                  Order from loosest to strictest:
                  <strong>Flexible (1d) → Moderate (5d) → Limited (14d) → Firm (30d)</strong>.
                  Stricter listings often earn more in older industry data, but most of that gap is selection
                  bias. Limited is the well-tested middle ground for most hosts.
                </div>
              </div>
            }
          </div>

          <!-- Seasonal cancellation policies (VRBO) -->
          <div class="settings-row">
            <div class="row-main">
              <div class="col-name">
                <span class="name-text">Seasonal cancellation</span>
                @if (seasonalCancellationRecoShort) {
                  <button type="button" class="reco-pill" [class.action]="seasonalCancellationRecommendation?.action" (click)="toggleInfo('seasonal-cancellation')">
                    <span class="reco-arrow">→</span>
                    <span>{{ seasonalCancellationRecoShort }}</span>
                  </button>
                }
              </div>
              <div class="col-state">
                <button type="button" class="state-switch" [class.on]="levers.seasonalCancellation" [class.off]="!levers.seasonalCancellation" (click)="toggleSeasonalCancellation()" [attr.aria-label]="'Seasonal cancellation: ' + (levers.seasonalCancellation ? 'ON' : 'OFF')">
                  <div class="switch-track"><div class="switch-thumb"></div></div>
                </button>
              </div>
              <div class="col-action">
                <button type="button" class="details-btn" (click)="toggleInfo('seasonal-cancellation')" [attr.aria-expanded]="expandedSettings.has('seasonal-cancellation')" aria-label="Toggle details">
                  <span class="details-chevron" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            @if (expandedSettings.has('seasonal-cancellation')) {
              <div class="row-details">
                @if (seasonalCancellationRecommendation) {
                  <div class="details-reco" [class.action]="seasonalCancellationRecommendation.action">
                    <span class="reco-label">Opportunity</span>
                    <span class="reco-text">{{ seasonalCancellationRecommendation.text }}</span>
                  </div>
                }
                <div class="lever-why">
                  Vrbo lets you apply different cancellation policies to different date ranges. Common use:
                  stricter policy (Firm / No Refund) for peak season + major holidays, loose policy
                  (Relaxed / Moderate) for shoulder season and weeknights. The platform applies the
                  policy based on the check-in date, not booking date.
                  <strong>Best for:</strong> properties with strong peak/off-peak demand cycles.
                  <strong>Note:</strong> Airbnb and Booking.com don't support seasonal cancellation
                  natively — you'd need to manually swap policies on those.
                </div>
              </div>
            }
          </div>

          <!-- Non-Refundable Rate -->
          <div class="settings-row">
            <div class="row-main">
              <div class="col-name">
                <span class="name-text">Non-Refundable Rate</span>
                @if (nonRefundableRecoShort) {
                  <button type="button" class="reco-pill" [class.action]="nonRefundableRecommendation?.action" (click)="toggleInfo('non-refundable')">
                    <span class="reco-arrow">→</span>
                    <span>{{ nonRefundableRecoShort }}</span>
                  </button>
                }
              </div>
              <div class="col-state">
                <button type="button" role="switch" class="state-switch"
                        [class.on]="allOn(nonRefundableBoolState)"
                        [attr.aria-checked]="allOn(nonRefundableBoolState)"
                        (click)="onSwitchClick('non-refundable')"
                        aria-label="Toggle Non-Refundable Rate">
                  <div class="switch-track"><div class="switch-thumb"></div></div>
                </button>
              </div>
              <div class="col-action">
                <button type="button" class="details-btn" (click)="toggleInfo('non-refundable')" [attr.aria-expanded]="expandedSettings.has('non-refundable')" aria-label="Toggle details">
                  <span class="details-chevron" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            @if (expandedSettings.has('non-refundable')) {
              <div class="row-details">
                @if (nonRefundableRecommendation) {
                  <div class="details-reco" [class.action]="nonRefundableRecommendation.action">
                    <span class="reco-label">Opportunity</span>
                    <span class="reco-text">{{ nonRefundableRecommendation.text }}</span>
                  </div>
                }
                <div class="lever-why">
                  <strong>Gap trade</strong> — the discount is worth it for guests only when your regular policy
                  gives them meaningful flexibility to give up. Strong on Flexible/Moderate; narrow on Limited;
                  near-zero on Firm. The discount is usually most useful when the standard policy is loose.
                </div>
              </div>
            }
          </div>

          <!-- Check-in / Check-out Days -->
          <div class="settings-row">
            <div class="row-main">
              <div class="col-name">
                <span class="name-text">Check-in / Check-out Days</span>
                @if (checkinCheckoutRecoShort) {
                  <button type="button" class="reco-pill" [class.action]="checkinCheckoutRecommendation?.action" (click)="toggleInfo('checkin-checkout')">
                    <span class="reco-arrow">→</span>
                    <span>{{ checkinCheckoutRecoShort }}</span>
                  </button>
                }
              </div>
              <div class="col-state">
                <button type="button" class="state-pill" (click)="toggleInfo('checkin-checkout')" aria-label="Edit check-in / check-out days">
                  <span>{{ hasCheckinRestrictions ? 'Restricted' : 'Open' }}</span>
                  <span class="chevron">▾</span>
                </button>
              </div>
              <div class="col-action">
                <button type="button" class="details-btn" (click)="toggleInfo('checkin-checkout')" [attr.aria-expanded]="expandedSettings.has('checkin-checkout')" aria-label="Toggle details">
                  <span class="details-chevron" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            @if (expandedSettings.has('checkin-checkout')) {
              <div class="row-details">
                @if (checkinCheckoutRecommendation) {
                  <div class="details-reco" [class.action]="checkinCheckoutRecommendation.action">
                    <span class="reco-label">Opportunity</span>
                    <span class="reco-text">{{ checkinCheckoutRecommendation.text }}</span>
                  </div>
                }
                <div class="details-action-row">
                  <tidy-text [actionBody]="'Edit blocked days'" (action)="requestLeverChange('checkin-checkout', null)" class="link" />
                </div>
                <div class="lever-why">
                  Blocks guests from starting or ending a stay on specific weekdays. Classic pattern:
                  <strong>Saturday-only check-in</strong> paired with a 7-night minimum for weekly stays.
                  Tight restrictions drop bookings 15-25%, usually offset by longer stays and cleaner
                  turnover savings.
                </div>
              </div>
            }
          </div>

          <!-- Check-in / Check-out times -->
          <div class="settings-row">
            <div class="row-main">
              <div class="col-name">
                <span class="name-text">Check-in / Check-out times</span>
              </div>
              <div class="col-state">
                <button type="button" class="state-pill policy" (click)="toggleInfo('checkin-times')" aria-label="Change check-in / check-out times">
                  <span>{{ checkInOutTimesLabel }}</span>
                  <span class="chevron">▾</span>
                </button>
              </div>
              <div class="col-action">
                <button type="button" class="details-btn" (click)="toggleInfo('checkin-times')" [attr.aria-expanded]="expandedSettings.has('checkin-times')" aria-label="Toggle details">
                  <span class="details-chevron" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            @if (expandedSettings.has('checkin-times')) {
              <div class="row-details">
                <div class="time-fields">
                  <label class="time-field">
                    <span class="time-field-label">Check-in after</span>
                    <select class="time-select" [value]="levers.checkInTime" (change)="setCheckInOutTime('in', $any($event.target).value)">
                      @for (opt of checkInTimeOptions; track opt.value) {
                        <option [value]="opt.value">{{ opt.label }}</option>
                      }
                    </select>
                  </label>
                  <label class="time-field">
                    <span class="time-field-label">Check-out by</span>
                    <select class="time-select" [value]="levers.checkOutTime" (change)="setCheckInOutTime('out', $any($event.target).value)">
                      @for (opt of checkOutTimeOptions; track opt.value) {
                        <option [value]="opt.value">{{ opt.label }}</option>
                      }
                    </select>
                  </label>
                </div>
              </div>
            }
          </div>

          <!-- Stay duration -->
          <div class="settings-row">
            <div class="row-main">
              <div class="col-name">
                <span class="name-text">Stay duration</span>
              </div>
              <div class="col-state">
                <button type="button" class="state-pill policy" (click)="toggleInfo('stay-duration')" aria-label="Change stay duration">
                  <span>{{ levers.minNights }}-{{ levers.maxNights }} nights</span>
                  <span class="chevron">▾</span>
                </button>
              </div>
              <div class="col-action">
                <button type="button" class="details-btn" (click)="toggleInfo('stay-duration')" [attr.aria-expanded]="expandedSettings.has('stay-duration')" aria-label="Toggle details">
                  <span class="details-chevron" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            @if (expandedSettings.has('stay-duration')) {
              <div class="row-details">
                <div class="duration-fields">
                  <label class="time-field">
                    <span class="time-field-label">Minimum</span>
                    <input class="time-select" type="number" min="1" step="1" [value]="levers.minNights" (change)="setStayLength('min-nights', $any($event.target).value)" />
                  </label>
                  <label class="time-field">
                    <span class="time-field-label">Maximum</span>
                    <input class="time-select" type="number" min="1" step="1" [value]="levers.maxNights" (change)="setStayLength('max-nights', $any($event.target).value)" />
                  </label>
                </div>
                <div class="lever-why">
                  Minimums help manage turnover. Maximums control whether longer stays can book.
                </div>
              </div>
            }
          </div>

          <!-- Advance notice -->
          <div class="settings-row">
            <div class="row-main">
              <div class="col-name">
                <span class="name-text">Advance notice</span>
                @if (advanceNoticeRecoShort) {
                  <button type="button" class="reco-pill" [class.action]="advanceNoticeRecommendation?.action" (click)="toggleInfo('advance-notice')">
                    <span class="reco-arrow">→</span>
                    <span>{{ advanceNoticeRecoShort }}</span>
                  </button>
                }
              </div>
              <div class="col-state">
                <button type="button" class="state-pill policy" (click)="toggleInfo('advance-notice')" aria-label="Change advance notice">
                  <span>{{ advanceNoticeLabel }}</span>
                  <span class="chevron">▾</span>
                </button>
              </div>
              <div class="col-action">
                <button type="button" class="details-btn" (click)="toggleInfo('advance-notice')" [attr.aria-expanded]="expandedSettings.has('advance-notice')" aria-label="Toggle details">
                  <span class="details-chevron" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            @if (expandedSettings.has('advance-notice')) {
              <div class="row-details">
                @if (advanceNoticeRecommendation) {
                  <div class="details-reco" [class.action]="advanceNoticeRecommendation.action">
                    <span class="reco-label">Opportunity</span>
                    <span class="reco-text">{{ advanceNoticeRecommendation.text }}</span>
                  </div>
                }
                <label class="number-field">
                  <span class="time-field-label">Days before check-in</span>
                  <input class="time-select" type="number" min="0" step="1" [value]="levers.advanceNoticeDays" (change)="setStayLength('advance-notice', $any($event.target).value)" />
                </label>
                <div class="lever-why">
                  How much lead time you need between booking and check-in. Same-day bookings can lift
                  occupancy 5-10% in last-minute markets (cities, business travel) but stress operations.
                  TIDY's auto-scheduled cleanings make same-day or 1-day lead time viable for most
                  properties.
                </div>
              </div>
            }
          </div>

          <!-- Preparation time -->
          <div class="settings-row">
            <div class="row-main">
              <div class="col-name">
                <span class="name-text">Preparation time</span>
                @if (preparationRecoShort) {
                  <button type="button" class="reco-pill" [class.action]="preparationRecommendation?.action" (click)="toggleInfo('preparation')">
                    <span class="reco-arrow">→</span>
                    <span>{{ preparationRecoShort }}</span>
                  </button>
                }
              </div>
              <div class="col-state">
                <button type="button" class="state-pill policy" (click)="toggleInfo('preparation')" aria-label="Change preparation time">
                  <span>{{ preparationLabel }}</span>
                  <span class="chevron">▾</span>
                </button>
              </div>
              <div class="col-action">
                <button type="button" class="details-btn" (click)="toggleInfo('preparation')" [attr.aria-expanded]="expandedSettings.has('preparation')" aria-label="Toggle details">
                  <span class="details-chevron" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            @if (expandedSettings.has('preparation')) {
              <div class="row-details">
                @if (preparationRecommendation) {
                  <div class="details-reco" [class.action]="preparationRecommendation.action">
                    <span class="reco-label">Opportunity</span>
                    <span class="reco-text">{{ preparationRecommendation.text }}</span>
                  </div>
                }
                <label class="number-field">
                  <span class="time-field-label">Nights blocked between bookings</span>
                  <input class="time-select" type="number" min="0" step="1" [value]="levers.preparationNights" (change)="setStayLength('preparation', $any($event.target).value)" />
                </label>
                <div class="lever-why">
                  Blocks N nights before and after each reservation so you / your cleaner has time to turn
                  the property. <strong>TIDY's edge:</strong> because we auto-schedule a cleaning
                  immediately at checkout, most properties can run with <strong>0 prep nights</strong> —
                  every extra prep night costs ~3% of your possible bookings.
                </div>
              </div>
            }
          </div>

          <!-- Availability window -->
          <div class="settings-row">
            <div class="row-main">
              <div class="col-name">
                <span class="name-text">Availability window</span>
                @if (availabilityRecoShort) {
                  <button type="button" class="reco-pill" [class.action]="availabilityRecommendation?.action" (click)="toggleInfo('availability')">
                    <span class="reco-arrow">→</span>
                    <span>{{ availabilityRecoShort }}</span>
                  </button>
                }
              </div>
              <div class="col-state">
                <button type="button" class="state-pill policy" (click)="toggleInfo('availability')" aria-label="Change availability window">
                  <span>{{ levers.availabilityMonths }} months</span>
                  <span class="chevron">▾</span>
                </button>
              </div>
              <div class="col-action">
                <button type="button" class="details-btn" (click)="toggleInfo('availability')" [attr.aria-expanded]="expandedSettings.has('availability')" aria-label="Toggle details">
                  <span class="details-chevron" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            @if (expandedSettings.has('availability')) {
              <div class="row-details">
                @if (availabilityRecommendation) {
                  <div class="details-reco" [class.action]="availabilityRecommendation.action">
                    <span class="reco-label">Opportunity</span>
                    <span class="reco-text">{{ availabilityRecommendation.text }}</span>
                  </div>
                }
                <label class="number-field">
                  <span class="time-field-label">Months in advance</span>
                  <input class="time-select" type="number" min="1" step="1" [value]="levers.availabilityMonths" (change)="setStayLength('availability', $any($event.target).value)" />
                </label>
                <div class="lever-why">
                  How far in advance guests can book. Longer windows surface in more searches and capture
                  early-booker demand (weddings, holidays, peak vacation planning). Trade-off: locking in
                  prices further out, harder to repivot if demand shifts.
                </div>
              </div>
            }
          </div>

        </div>
      </tidy-card>

      <!-- Price range -->
      <tidy-card>
        <tidy-row class="extra-bottom-padding">
          <tidy-text [header]="'Price range'" />
        </tidy-row>
        <div class="price-tiles">
          <label class="price-tile editable" aria-label="Minimum price">
            <span class="tile-label">Min</span>
            <span class="price-input">
              <span>$</span>
              <input type="number" min="0" step="1" [value]="formatDollarsInput(customization.minPrice)" (change)="setPriceRange('min', $any($event.target).value)" />
            </span>
          </label>
          <label class="price-tile editable" aria-label="Maximum price">
            <span class="tile-label">Max</span>
            <span class="price-input">
              <span>$</span>
              <input type="number" min="0" step="1" [value]="formatDollarsInput(customization.maxPrice)" (change)="setPriceRange('max', $any($event.target).value)" />
            </span>
          </label>
        </div>
      </tidy-card>

      <!-- Discounts -->
      <tidy-card class="settings-card">
        <tidy-row class="extra-bottom-padding settings-card-header">
          <div class="settings-heading">
            <tidy-text [header]="'Discounts & promotions'" />
            <tidy-text [helper]="'Set discount rates and promotion windows.'" />
          </div>
        </tidy-row>
        <div class="settings-table">
          @for (promo of promotionControls; track promo.key) {
            <div class="settings-row">
              <div class="row-main">
                <div class="col-name">
                  <span class="name-text">{{ promo.name }}</span>
                  <span class="name-suffix">· {{ promo.note }}</span>
                </div>
                <div class="col-state">
                  <button type="button" class="state-pill" (click)="toggleInfo(promotionSettingKey(promo))" [attr.aria-label]="'Edit ' + promo.name">
                    <span>{{ promo.status }}</span>
                    <span class="chevron">▾</span>
                  </button>
                </div>
                <div class="col-action">
                  <button type="button" class="details-btn" (click)="toggleInfo(promotionSettingKey(promo))" [attr.aria-expanded]="expandedSettings.has(promotionSettingKey(promo))" aria-label="Toggle details">
                    <span class="details-chevron" aria-hidden="true"></span>
                  </button>
                </div>
              </div>
              @if (expandedSettings.has(promotionSettingKey(promo))) {
                <div class="row-details">
                  <div class="promotion-fields">
                    @if (promo.discountPercent !== undefined) {
                      <label class="number-field">
                        <span class="time-field-label">Discount %</span>
                        <input class="time-select" type="number" min="0" max="100" step="1" [value]="promo.discountPercent" (change)="setPromotionDiscount(promo, $any($event.target).value)" />
                      </label>
                    }
                    @if (promo.thresholdDays !== undefined) {
                      <label class="number-field">
                        <span class="time-field-label">{{ promo.thresholdLabel ?? 'Booking window' }}</span>
                        <input class="time-select" type="number" min="1" step="1" [value]="promo.thresholdDays" (change)="setPromotionThreshold(promo, $any($event.target).value)" />
                      </label>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </tidy-card>

      <!-- Date overrides -->
      <tidy-card>
        <tidy-row [alignLastItemRight]="true" class="extra-bottom-padding">
          <tidy-text [header]="'Date overrides'" />
          <tidy-text [helper]="customization.overrides.length + ' active'" />
        </tidy-row>
        @if (customization.overrides.length > 0) {
          <div class="overrides-table">
            <div class="override-row override-header">
              <div>Date</div>
              <div class="text-right">Price</div>
              <div class="text-right">Min stay</div>
            </div>
            @for (override of customization.overrides; track override.date) {
              <div class="override-row">
                <div>{{ formatDate(override.date) }}</div>
                <div class="text-right">{{ formatOverridePrice(override) }}</div>
                <div class="text-right">{{ override.minStay !== null ? override.minStay + 'n' : '—' }}</div>
              </div>
            }
          </div>
        } @else {
          <tidy-row>
            <tidy-text [body]="'No date overrides yet. Tap any day on the calendar to add one.'" />
          </tidy-row>
        }
      </tidy-card>
    }
  `,
  styles: [`
    /* Booking settings */
    :host ::ng-deep tidy-card.settings-card {
      overflow: visible;
    }
    .settings-card-header {
      padding-bottom: 14px;
    }
    :host ::ng-deep .settings-card-header > .row-component {
      align-items: center;
      gap: 12px;
    }
    .settings-heading {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
      padding-right: 16px;
    }
    .settings-table {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }
    .settings-row {
      display: flex;
      flex-direction: column;
      background: var(--tidy-bg-card);
      border: 1px solid var(--tidy-border-light);
      border-radius: var(--tidy-radius-md);
      min-height: 86px;
      transition: background-color 120ms ease, border-color 120ms ease;
    }
    .settings-row:hover {
      background: var(--tidy-bg-hover);
      border-color: var(--tidy-border-color);
    }
    .row-main {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto 30px;
      gap: 12px;
      align-items: start;
      padding: 14px;
    }
    .col-name {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
      min-width: 0;
    }
    .name-text {
      font-size: 14px;
      font-weight: 600;
      line-height: 1.25;
    }
    .name-suffix {
      font-size: 11px;
      font-weight: 400;
      color: var(--tidy-text-muted);
    }

    .reco-pill {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      max-width: 100%;
      padding: 0;
      background: transparent;
      border: none;
      border-radius: 0;
      font-size: 12px;
      font-weight: 500;
      color: var(--tidy-text-muted);
      cursor: pointer;
      line-height: 1.35;
      transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
      white-space: normal;
      text-align: left;
    }
    .reco-pill:hover {
      color: var(--tidy-text-primary);
    }
    .reco-arrow { font-size: 10px; opacity: 0.7; }

    .col-state {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      min-width: 74px;
      padding-top: 1px;
    }

    .state-switch {
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px 0;
      display: inline-flex;
      align-items: center;
    }
    .switch-track {
      position: relative;
      width: 32px;
      height: 18px;
      background: var(--tidy-bg-input);
      border-radius: 9px;
      border: 1px solid var(--tidy-border-light);
      transition: background-color 180ms ease;
    }
    .switch-thumb {
      position: absolute;
      top: 1px;
      left: 1px;
      width: 14px;
      height: 14px;
      background: white;
      border-radius: 50%;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
      transition: transform 180ms ease, background-color 180ms ease;
    }
    .state-switch.on .switch-track {
      background: var(--tidy-success);
      border-color: var(--tidy-success);
    }
    .state-switch.on .switch-thumb { transform: translateX(14px); }

    /* Compact switch for sub-toggles in expanded panels */
    .state-switch.compact .switch-track {
      width: 28px;
      height: 16px;
      border-radius: 8px;
    }
    .state-switch.compact .switch-thumb {
      width: 12px;
      height: 12px;
    }
    .state-switch.compact.on .switch-thumb {
      transform: translateX(12px);
    }

    .state-pill {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      min-height: 26px;
      padding: 4px 10px;
      background: var(--tidy-bg-input);
      border: 1px solid var(--tidy-border-light);
      border-radius: var(--tidy-radius-full);
      font-size: 12px;
      font-weight: 600;
      color: var(--tidy-text-primary);
      cursor: pointer;
      transition: background-color 120ms ease, border-color 120ms ease;
      white-space: nowrap;
    }
    .state-pill:hover {
      background: var(--tidy-bg-hover);
      border-color: var(--tidy-border-color);
    }
    .chevron { font-size: 9px; opacity: 0.7; }

    .col-action {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
    .details-btn {
      background: transparent;
      border: 1px solid transparent;
      cursor: pointer;
      padding: 0;
      width: 26px;
      height: 26px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--tidy-radius-full);
      color: var(--tidy-text-muted);
      transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
    }
    .details-btn:hover {
      background: var(--tidy-bg-hover);
      color: var(--tidy-text-primary);
    }
    .details-btn[aria-expanded="true"] {
      background: var(--tidy-bg-hover);
      border-color: var(--tidy-border-color);
      color: var(--tidy-text-primary);
    }
    .details-chevron {
      width: 8px;
      height: 8px;
      border-right: 1.5px solid currentColor;
      border-bottom: 1.5px solid currentColor;
      transform: translateY(-2px) rotate(45deg);
      transition: transform 120ms ease;
    }
    .details-btn[aria-expanded="true"] .details-chevron {
      transform: translateY(2px) rotate(225deg);
    }

    .row-details {
      padding: 0 14px 14px;
      background: transparent;
      border-top: 1px solid var(--tidy-border-light);
      margin-top: auto;
    }
    .details-reco {
      display: flex;
      align-items: baseline;
      gap: 8px;
      padding: 8px 10px;
      margin-bottom: 10px;
      background: var(--tidy-bg-input);
      border-left: 3px solid var(--tidy-border-color);
      border-radius: 4px;
      font-size: 12px;
      line-height: 1.45;
    }
    .reco-label {
      flex-shrink: 0;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0;
      color: var(--tidy-text-muted);
      text-transform: uppercase;
    }
    .reco-text { color: var(--tidy-text-primary); }

    .lever-why {
      font-size: 11px;
      color: var(--tidy-text-muted);
      line-height: 1.55;
    }
    .lever-why strong { color: var(--tidy-text-primary); font-weight: 600; }
    .details-action-row { margin: 10px 0; }

    /* Value picker (cancellation, stay-length, etc.) */
    .value-picker {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
      gap: 6px;
      margin-bottom: 10px;
    }
    .picker-option {
      padding: 8px 10px;
      background: var(--tidy-bg-input);
      border: 1px solid var(--tidy-border-light);
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 120ms ease, border-color 120ms ease;
      text-align: center;
    }
    .picker-option:hover { background: var(--tidy-bg-hover); }
    .picker-option.active {
      background: var(--tidy-bg-card);
      border-color: var(--tidy-border-color);
    }
    .picker-name { font-size: 12px; font-weight: 700; margin-bottom: 1px; }
    .picker-subtitle { font-size: 10px; color: var(--tidy-text-muted); margin-bottom: 4px; }
    .picker-lift {
      font-size: 11px;
      font-weight: 700;
      color: var(--tidy-text-muted);
    }

    .time-fields,
    .duration-fields {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
      margin-bottom: 10px;
    }
    .number-field,
    .time-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .number-field {
      max-width: 180px;
      margin-bottom: 10px;
    }
    .time-field-label {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0;
      color: var(--tidy-text-muted);
      text-transform: uppercase;
    }
    .time-select {
      width: 100%;
      height: 36px;
      padding: 0 10px;
      background: var(--tidy-bg-card);
      border: 1px solid var(--tidy-border-light);
      border-radius: var(--tidy-radius-md);
      color: var(--tidy-text-primary);
      font-size: 13px;
      font-weight: 700;
    }

    /* Sub-toggles inside expanded panels (Instant Book filters, etc.) */
    .sub-toggles {
      background: var(--tidy-bg-input);
      border: 1px solid var(--tidy-border-light);
      border-radius: 6px;
      padding: 4px 0;
      margin-bottom: 10px;
    }
    .sub-toggle-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 8px 10px;
      border-bottom: 1px solid var(--tidy-border-light);
    }
    .sub-toggle-row:last-child { border-bottom: none; }
    .sub-toggle-copy {
      min-width: 0;
    }
    .sub-toggle-name {
      font-size: 12px;
      font-weight: 500;
    }
    .sub-toggle-note {
      margin-top: 2px;
      font-size: 11px;
      line-height: 1.35;
      color: var(--tidy-text-muted);
    }
    .money-input {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      min-width: 96px;
      height: 34px;
      padding: 0 10px;
      background: var(--tidy-bg-card);
      border: 1px solid var(--tidy-border-light);
      border-radius: var(--tidy-radius-md);
      font-size: 13px;
      font-weight: 700;
      color: var(--tidy-text-primary);
    }
    .money-input input {
      width: 58px;
      border: none;
      outline: none;
      background: transparent;
      font: inherit;
      color: inherit;
    }

    @media (max-width: 900px) {
      :host ::ng-deep .settings-card-header > .row-component {
        align-items: flex-start;
      }
      .settings-table {
        grid-template-columns: 1fr;
      }
      .row-main {
        grid-template-columns: minmax(0, 1fr) auto 28px;
        gap: 10px;
        padding: 12px;
      }
      .row-details {
        padding-left: 12px;
        padding-right: 12px;
      }
      .col-name { gap: 6px; }
    }

    .price-tiles { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
    .price-tile {
      padding: 8px 10px;
      background: var(--tidy-bg-input);
      border-radius: 6px;
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 8px;
    }
    .price-tile.editable {
      cursor: text;
    }
    .price-tile.editable:focus-within {
      outline: 1px solid var(--tidy-accent-border);
      background: var(--tidy-bg-card);
    }
    .price-input {
      display: inline-flex;
      align-items: baseline;
      justify-content: flex-end;
      min-width: 96px;
      font-size: 15px;
      font-weight: 700;
      line-height: 1;
      color: var(--tidy-text-primary);
    }
    .price-input input {
      width: 72px;
      border: none;
      outline: none;
      background: transparent;
      color: inherit;
      font: inherit;
      text-align: right;
    }
    .tile-label {
      font-size: 10px;
      font-weight: 700;
      color: var(--tidy-text-muted);
      text-transform: uppercase;
      letter-spacing: 0;
    }
    .tile-value { font-size: 15px; font-weight: 700; line-height: 1; }
    .promotion-fields {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 180px));
      gap: 10px;
    }
    @media (max-width: 560px) {
      .promotion-fields {
        grid-template-columns: 1fr;
      }
    }
    .overrides-table { display: flex; flex-direction: column; gap: 2px; }
    .override-row {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr;
      gap: 8px;
      padding: 10px 4px;
      align-items: center;
      border-bottom: 1px solid var(--tidy-border-color);
    }
    .override-row:last-child { border-bottom: none; }
    .override-row.override-header {
      font-size: 11px;
      font-weight: 600;
      color: var(--tidy-text-muted);
      text-transform: uppercase;
      letter-spacing: 0;
    }
    .text-right { text-align: right; }
  `]
})
export class PricingCustomizationComponent {
  address = input.required<any>();
  mock = input<boolean>(false);
  loaded = output<void>();

  private readonly pricingService = inject(MarketingPricingService);
  private readonly conciergeApi = inject(ConciergeApiService);
  private readonly util = inject(Util);
  private readonly cdr = inject(ChangeDetectorRef);

  customization: PricingCustomization;
  expandedSettings = new Set<string>();
  petFeeAmount = 0;
  promotionEdits: Partial<Record<PromotionKey, PromotionEdit>> = {};

  levers: RevenueLeverState = {
    instantBook: { airbnb: true, booking: true, vrbo: true },
    petFriendly: { airbnb: false, booking: false, vrbo: false },
    cancellationPolicy: { airbnb: 'moderate', booking: 'moderate', vrbo: 'moderate' },
    nonRefundable: { airbnb: false, booking: false, vrbo: false, bookingPercent: 10, vrboPercent: 10 },
    checkInBlocked: [],
    checkOutBlocked: [],
    minNights: 2,
    maxNights: 30,
    advanceNoticeDays: 1,
    preparationNights: 0,
    availabilityMonths: 12,
    guestFilters: { verifiedId: false, profilePhoto: false, goodTrackRecord: false },
    seasonalCancellation: false,
    checkInTime: '15:00',
    checkOutTime: '11:00'
  };

  readonly weekdayLabels: { value: Weekday; short: string; full: string }[] = [
    { value: 'sun', short: 'Sun', full: 'Sunday' },
    { value: 'mon', short: 'Mon', full: 'Monday' },
    { value: 'tue', short: 'Tue', full: 'Tuesday' },
    { value: 'wed', short: 'Wed', full: 'Wednesday' },
    { value: 'thu', short: 'Thu', full: 'Thursday' },
    { value: 'fri', short: 'Fri', full: 'Friday' },
    { value: 'sat', short: 'Sat', full: 'Saturday' }
  ];

  formatRestrictionSummary(blocked: Weekday[]): string {
    if (!blocked.length) {return 'All days allowed';}
    const allowed = this.weekdayLabels.filter(d => !blocked.includes(d.value)).map(d => d.short);
    if (allowed.length === 0) {return 'No days allowed';}
    if (allowed.length === 1) {return `${allowed[0]} only`;}
    if (allowed.length === 7) {return 'All days allowed';}
    return `${allowed.join(', ')} only`;
  }

  readonly cancellationOptions: { value: CancellationPolicy; label: string; subtitle: string; liftLabel: string; liftMax: number }[] = [
    { value: 'flexible', label: 'Flexible', subtitle: '1d notice',  liftLabel: 'Baseline',    liftMax: 0  },
    { value: 'moderate', label: 'Moderate', subtitle: '5d notice',  liftLabel: '−2 to +5%',   liftMax: 5  },
    { value: 'limited',  label: 'Limited',  subtitle: '14d notice', liftLabel: '+0 to +12%',  liftMax: 12 },
    { value: 'firm',     label: 'Firm',     subtitle: '30d notice', liftLabel: '−5 to +5%',   liftMax: 5  }
  ];
  formatPolicy(policy: CancellationPolicy): string {
    return this.cancellationOptions.find(o => o.value === policy)?.label ?? policy;
  }

  readonly checkInTimeOptions: { value: string; label: string }[] = [
    { value: '12:00', label: '12 PM' },
    { value: '13:00', label: '1 PM' },
    { value: '14:00', label: '2 PM' },
    { value: '15:00', label: '3 PM' },
    { value: '16:00', label: '4 PM' },
    { value: '17:00', label: '5 PM' },
    { value: '18:00', label: '6 PM' },
    { value: 'flexible', label: 'Flexible' }
  ];
  readonly checkOutTimeOptions: { value: string; label: string }[] = [
    { value: '09:00', label: '9 AM' },
    { value: '10:00', label: '10 AM' },
    { value: '11:00', label: '11 AM' },
    { value: '12:00', label: '12 PM' }
  ];

  formatTime(value: string): string {
    return this.checkInTimeOptions.find(o => o.value === value)?.label
        ?? this.checkOutTimeOptions.find(o => o.value === value)?.label
        ?? value;
  }

  get checkInOutTimesLabel(): string {
    return `${this.formatTime(this.levers.checkInTime)} / ${this.formatTime(this.levers.checkOutTime)}`;
  }

  setCheckInOutTime(which: 'in' | 'out', value: string) {
    const subject = `Set check-${which} time to ${this.formatTime(value)}`;
    if (which === 'in') {this.levers.checkInTime = value;}
    else {this.levers.checkOutTime = value;}
    this.cdr.markForCheck();
    if (this.mock()) {
      this.showChangeInProgress();
      return;
    }
    this.fireConciergeRequest(subject, { lever: `check-${which}-time`, next_value: value });
  }

  allOn(state: { airbnb: boolean; booking: boolean; vrbo: boolean }): boolean {
    return state.airbnb && state.booking && state.vrbo;
  }
  allOff(state: { airbnb: boolean; booking: boolean; vrbo: boolean }): boolean {
    return !state.airbnb && !state.booking && !state.vrbo;
  }
  anyOn(state: { airbnb: boolean; booking: boolean; vrbo: boolean }): boolean {
    return state.airbnb || state.booking || state.vrbo;
  }

  get nonRefundableBoolState() {
    return { airbnb: this.levers.nonRefundable.airbnb, booking: this.levers.nonRefundable.booking, vrbo: this.levers.nonRefundable.vrbo };
  }
  get hasCheckinRestrictions(): boolean {
    return this.levers.checkInBlocked.length > 0 || this.levers.checkOutBlocked.length > 0;
  }
  get guestFiltersCount(): number {
    const f = this.levers.guestFilters;
    return (f.verifiedId ? 1 : 0) + (f.profilePhoto ? 1 : 0) + (f.goodTrackRecord ? 1 : 0);
  }

  toggleInfo(key: string) {
    if (this.expandedSettings.has(key)) {
      this.expandedSettings.delete(key);
    } else {
      this.expandedSettings.add(key);
    }
    this.cdr.markForCheck();
  }

  onSwitchClick(key: 'instant-book' | 'pet-friendly' | 'non-refundable') {
    const state = this.boolStateFor(key);
    this.requestLeverChange(key, !this.allOn(state));
  }

  private boolStateFor(key: 'instant-book' | 'pet-friendly' | 'non-refundable') {
    if (key === 'instant-book') {return this.levers.instantBook;}
    if (key === 'pet-friendly') {return this.levers.petFriendly;}
    return this.nonRefundableBoolState;
  }

  get cancellationPillLabel(): string {
    return this.formatPolicy(this.levers.cancellationPolicy.airbnb);
  }

  get advanceNoticeLabel(): string {
    if (this.levers.advanceNoticeDays === 0) {return 'Same day';}
    return `${this.levers.advanceNoticeDays} ${this.levers.advanceNoticeDays === 1 ? 'day' : 'days'}`;
  }
  get preparationLabel(): string {
    if (this.levers.preparationNights === 0) {return 'None';}
    return `${this.levers.preparationNights} ${this.levers.preparationNights === 1 ? 'night' : 'nights'}`;
  }

  get promotionControls(): PromotionControl[] {
    const weekly = this.customization?.weeklyDiscountPercent;
    const monthly = this.customization?.monthlyDiscountPercent;
    const lastMinute = this.customization?.lastMinuteRule;
    const farOut = this.customization?.farOutRule;
    const weeklyValue = this.promotionValue('weekly-stays', this.absPercent(weekly, 10), !!weekly);
    const monthlyValue = this.promotionValue('monthly-stays', this.absPercent(monthly, 25), !!monthly);
    const lastMinuteDays = this.promotionThreshold('last-minute-booking', lastMinute?.thresholdDays ?? 7);
    const lastMinuteValue = this.promotionValue('last-minute-booking', this.absPercent(lastMinute?.adjustmentPercent, 20), !!lastMinute);
    const farOutDays = this.promotionThreshold('far-out-booking', farOut?.thresholdDays ?? 180);
    const farOutValue = this.promotionValue('far-out-booking', this.absPercent(farOut?.adjustmentPercent, 5), !!farOut);
    const memberValue = this.promotionValue('member-only-exclusive', 10, false);
    const mobileValue = this.promotionValue('mobile-rate', 10, false);
    const flashValue = this.promotionValue('flash-campaign-deal', 15, false);
    const bookingDealValue = this.promotionValue('booking-basic-deal', 10, false);
    const geniusValue = this.promotionValue('genius-loyalty', 10, true);

    return [
      {
        key: 'weekly-stays',
        name: 'Weekly stays',
        status: weeklyValue.status,
        note: '7+ nights',
        platforms: ['airbnb', 'vrbo'],
        active: weeklyValue.active,
        discountPercent: weeklyValue.discountPercent
      },
      {
        key: 'monthly-stays',
        name: 'Monthly stays',
        status: monthlyValue.status,
        note: '28+ nights',
        platforms: ['airbnb', 'vrbo'],
        active: monthlyValue.active,
        discountPercent: monthlyValue.discountPercent
      },
      {
        key: 'last-minute-booking',
        name: 'Last-minute booking',
        status: lastMinuteValue.status,
        note: `Within ${lastMinuteDays} days`,
        platforms: ['booking', 'vrbo', 'airbnb'],
        active: lastMinuteValue.active,
        discountPercent: lastMinuteValue.discountPercent,
        thresholdDays: lastMinuteDays,
        thresholdLabel: 'Days before check-in'
      },
      {
        key: 'far-out-booking',
        name: 'Far-out booking',
        status: farOutValue.status,
        note: `Beyond ${farOutDays} days`,
        platforms: ['booking', 'vrbo', 'airbnb'],
        active: farOutValue.active,
        discountPercent: farOutValue.discountPercent,
        thresholdDays: farOutDays,
        thresholdLabel: 'Days in advance'
      },
      {
        key: 'member-only-exclusive',
        name: 'Member-only exclusive',
        status: memberValue.status,
        note: 'Member-only deal',
        platforms: ['vrbo'],
        active: memberValue.active,
        discountPercent: memberValue.discountPercent
      },
      {
        key: 'mobile-rate',
        name: 'Mobile-only rate',
        status: mobileValue.status,
        note: 'Mobile app / mobile web',
        platforms: ['booking', 'vrbo'],
        active: mobileValue.active,
        discountPercent: mobileValue.discountPercent
      },
      {
        key: 'flash-campaign-deal',
        name: 'Flash / campaign deal',
        status: flashValue.status,
        note: 'Limited-time sale',
        platforms: ['booking', 'vrbo'],
        active: flashValue.active,
        discountPercent: flashValue.discountPercent
      },
      {
        key: 'booking-basic-deal',
        name: 'Booking.com basic deal',
        status: bookingDealValue.status,
        note: 'Custom promotion',
        platforms: ['booking'],
        active: bookingDealValue.active,
        discountPercent: bookingDealValue.discountPercent
      },
      {
        key: 'genius-loyalty',
        name: 'Genius loyalty',
        status: geniusValue.status,
        note: 'Booking.com member discount',
        platforms: ['booking'],
        active: geniusValue.active,
        discountPercent: geniusValue.discountPercent
      }
    ];
  }

  promotionSettingKey(promo: PromotionControl): string {
    return `promotion-${promo.key}`;
  }

  private promotionValue(key: PromotionKey, fallbackDiscount: number, fallbackActive: boolean): { discountPercent: number; active: boolean; status: string } {
    const edit = this.promotionEdits[key] ?? {};
    const discountPercent = edit.discountPercent ?? (fallbackActive ? fallbackDiscount : 0);
    const active = discountPercent > 0;
    return {
      discountPercent,
      active,
      status: `${discountPercent}%`
    };
  }

  private promotionThreshold(key: PromotionKey, fallbackDays: number): number {
    return this.promotionEdits[key]?.thresholdDays ?? fallbackDays;
  }

  private absPercent(value: number | null | undefined, fallback: number): number {
    if (value === null || value === undefined) {return fallback;}
    return Math.abs(Math.round(value));
  }

  // Condensed reco labels (next to name)
  get instantBookRecoShort(): string | null {
    if (this.allOn(this.levers.instantBook)) {return null;}
    return 'Improve revenue 3-15%';
  }
  get petFriendlyRecoShort(): string | null {
    if (this.allOn(this.levers.petFriendly)) {return null;}
    return 'Improve revenue 5-15%';
  }
  get cancellationRecoShort(): string | null {
    return this.cancellationRecommendation ? 'Improve revenue up to 12%' : null;
  }
  get seasonalCancellationRecoShort(): string | null {
    return null;
  }
  get nonRefundableRecoShort(): string | null {
    return this.nonRefundableRecommendation ? 'Improve revenue 3-8%' : null;
  }
  get checkinCheckoutRecoShort(): string | null {
    return null;
  }
  get minNightsRecoShort(): string | null {
    return null;
  }
  get maxNightsRecoShort(): string | null {
    return null;
  }
  get advanceNoticeRecoShort(): string | null {
    if (this.levers.advanceNoticeDays >= 3) {return 'Improve revenue 5-10%';}
    return null;
  }
  get preparationRecoShort(): string | null {
    if (this.levers.preparationNights > 0) {return 'Improve revenue ~3%';}
    return null;
  }
  get availabilityRecoShort(): string | null {
    return null;
  }

  // Full recommendations
  get instantBookRecommendation(): { text: string; action: boolean } | null {
    const state = this.levers.instantBook;
    if (this.allOn(state)) {return null;}
    return { text: 'Improve revenue 3-15% by enabling Instant Book.', action: true };
  }
  get petFriendlyRecommendation(): { text: string; action: boolean } | null {
    const state = this.levers.petFriendly;
    if (this.allOn(state)) {return null;}
    return { text: 'Improve revenue 5-15% by allowing pets where the property can support it.', action: true };
  }
  get cancellationRecommendation(): { text: string; action: boolean } | null {
    const c = this.levers.cancellationPolicy;
    const allLimited = c.airbnb === 'limited' && c.booking === 'limited' && c.vrbo === 'limited';
    if (allLimited) {return null;}
    return { text: 'Improve revenue up to 12% by moving toward Limited cancellation.', action: true };
  }
  get seasonalCancellationRecommendation(): { text: string; action: boolean } | null {
    return null;
  }
  get nonRefundableRecommendation(): { text: string; action: boolean } | null {
    const policy = this.levers.cancellationPolicy.airbnb;
    const state = this.nonRefundableBoolState;
    if (!this.allOff(state)) {return null;}
    if (policy === 'flexible' || policy === 'moderate') {return { text: 'Improve revenue 3-8% with a non-refundable rate when the standard policy is loose.', action: true };}
    return null;
  }
  get checkinCheckoutRecommendation(): { text: string; action: boolean } | null {
    return null;
  }
  get minNightsRecommendation(): { text: string; action: boolean } | null {
    return null;
  }
  get maxNightsRecommendation(): { text: string; action: boolean } | null {
    return null;
  }
  get advanceNoticeRecommendation(): { text: string; action: boolean } | null {
    if (this.levers.advanceNoticeDays < 3) {return null;}
    return { text: 'Improve revenue 5-10% by allowing shorter-notice bookings.', action: true };
  }
  get preparationRecommendation(): { text: string; action: boolean } | null {
    if (this.levers.preparationNights === 0) {return null;}
    return { text: 'Improve revenue ~3% by removing each prep-night buffer.', action: true };
  }
  get availabilityRecommendation(): { text: string; action: boolean } | null {
    return null;
  }

  // Switch / picker handlers
  toggleGuestFilter(filter: GuestFilterKey) {
    const next = !this.levers.guestFilters[filter];
    const labels: Record<GuestFilterKey, string> = {
      verifiedId: 'Require verified government ID',
      profilePhoto: 'Require profile photo',
      goodTrackRecord: 'Require good track record'
    };
    const subject = `${next ? 'Enable' : 'Disable'} Instant Book filter — ${labels[filter]}`;
    this.levers.guestFilters = { ...this.levers.guestFilters, [filter]: next };
    this.cdr.markForCheck();
    if (this.mock()) {
      this.showChangeInProgress();
      return;
    }
    this.fireConciergeRequest(subject, { lever: 'guest-filter', filter, next_value: next });
  }

  toggleSeasonalCancellation() {
    const next = !this.levers.seasonalCancellation;
    const subject = `${next ? 'Enable' : 'Disable'} seasonal cancellation policies on Vrbo`;
    this.levers.seasonalCancellation = next;
    this.cdr.markForCheck();
    if (this.mock()) {
      this.showChangeInProgress();
      return;
    }
    this.fireConciergeRequest(subject, { lever: 'seasonal-cancellation', next_value: next });
  }

  setStayLength(key: StayLengthKey, rawValue: number | string) {
    const minValue = key === 'advance-notice' || key === 'preparation' ? 0 : 1;
    let value = Math.floor(Number(rawValue));
    if (!Number.isFinite(value)) {value = minValue;}
    value = Math.max(minValue, value);

    if (key === 'min-nights') {
      this.levers.minNights = value;
      if (this.levers.maxNights < value) {this.levers.maxNights = value;}
    }
    if (key === 'max-nights') {
      this.levers.maxNights = Math.max(value, this.levers.minNights);
    }
    if (key === 'advance-notice') {this.levers.advanceNoticeDays = value;}
    if (key === 'preparation') {this.levers.preparationNights = value;}
    if (key === 'availability') {this.levers.availabilityMonths = value;}

    const subjects: Record<StayLengthKey, string> = {
      'min-nights': `Set minimum nights to ${this.levers.minNights}`,
      'max-nights': `Set maximum nights to ${this.levers.maxNights}`,
      'advance-notice': `Set advance notice to ${this.advanceNoticeLabel}`,
      'preparation': `Set preparation time to ${this.preparationLabel}`,
      'availability': `Set availability window to ${this.levers.availabilityMonths} months`
    };
    const subject = subjects[key];
    this.cdr.markForCheck();
    if (this.mock()) {
      this.showChangeInProgress();
      return;
    }
    this.fireConciergeRequest(subject, { lever: key, next_value: value });
  }

  async requestLeverChange(lever: 'instant-book' | 'pet-friendly' | 'cancellation' | 'non-refundable' | 'checkin-checkout', nextValue: boolean | CancellationPolicy | null) {
    const subjects: Record<string, string> = {
      'instant-book': `Toggle Instant Book ${nextValue ? 'on' : 'off'}`,
      'pet-friendly': `Toggle Pet-Friendly ${nextValue ? 'on' : 'off'}`,
      'cancellation': `Change Cancellation Policy to ${nextValue}`,
      'non-refundable': `Toggle Non-Refundable Rate ${nextValue ? 'on' : 'off'}`,
      'checkin-checkout': 'Change check-in / check-out day restrictions'
    };
    const subject = subjects[lever];
    if (lever === 'instant-book') {const next = nextValue as boolean; this.levers.instantBook = { airbnb: next, booking: next, vrbo: next };}
    if (lever === 'pet-friendly') {const next = nextValue as boolean; this.levers.petFriendly = { airbnb: next, booking: next, vrbo: next };}
    if (lever === 'cancellation') {const next = nextValue as CancellationPolicy; this.levers.cancellationPolicy = { airbnb: next, booking: next, vrbo: next };}
    if (lever === 'non-refundable') {const next = nextValue as boolean; this.levers.nonRefundable = { ...this.levers.nonRefundable, airbnb: next, booking: next, vrbo: next };}
    this.cdr.markForCheck();
    if (this.mock()) {
      this.showChangeInProgress();
      return;
    }
    this.fireConciergeRequest(subject, { lever, next_value: nextValue });
  }

  setPetFeeAmount(value: string) {
    const amount = Math.max(0, Number(value) || 0);
    this.petFeeAmount = amount;
    this.cdr.markForCheck();
    if (this.mock()) {
      this.showChangeInProgress();
      return;
    }
    this.fireConciergeRequest('Change pet fee', { kind: 'platform-setting', setting_name: 'Pet fee', next_value: amount });
  }

  setPriceRange(key: PriceRangeKey, rawValue: string | number) {
    const cents = this.parseDollarInput(rawValue);
    if (cents === null) {
      this.util.showError('Enter a valid dollar amount.');
      this.cdr.markForCheck();
      return;
    }

    const currentMin = this.customization.minPrice ?? 0;
    const currentMax = this.customization.maxPrice ?? 0;
    const nextMin = key === 'min' ? cents : currentMin;
    const nextMax = key === 'max' ? cents : currentMax;

    if (nextMin > nextMax) {
      this.util.showError('Minimum price cannot be higher than maximum price.');
      this.cdr.markForCheck();
      return;
    }

    this.customization = {
      ...this.customization,
      minPrice: nextMin,
      maxPrice: nextMax
    };
    this.cdr.markForCheck();

    const subject = key === 'min'
      ? `Set minimum price to ${this.formatCents(nextMin)}`
      : `Set maximum price to ${this.formatCents(nextMax)}`;
    if (this.mock()) {
      this.showChangeInProgress();
      return;
    }
    this.fireConciergeRequest(subject, {
      kind: 'price-range',
      field: key === 'min' ? 'min_price' : 'max_price',
      next_value: key === 'min' ? nextMin : nextMax,
      min_price: nextMin,
      max_price: nextMax
    });
  }

  private parseDollarInput(rawValue: string | number): number | null {
    const value = Number(String(rawValue).replace(/[$,\s]/g, ''));
    if (!Number.isFinite(value) || value < 0) {return null;}
    return Math.round(value) * 100;
  }

  setPromotionDiscount(promo: PromotionControl, rawValue: string | number) {
    const discountPercent = this.parseWholeNumber(rawValue, 0, 100);
    if (discountPercent === null) {
      this.util.showError('Enter a discount between 0 and 100%.');
      this.cdr.markForCheck();
      return;
    }

    const existing = this.promotionEdits[promo.key] ?? {};
    const nextEdit: PromotionEdit = {
      ...existing,
      discountPercent,
      thresholdDays: existing.thresholdDays ?? promo.thresholdDays
    };
    this.promotionEdits = { ...this.promotionEdits, [promo.key]: nextEdit };
    this.applyPromotionEditToCustomization(promo.key, nextEdit);
    this.cdr.markForCheck();

    const subject = discountPercent > 0
      ? `Set ${promo.name} discount to ${discountPercent}%`
      : `Turn off ${promo.name}`;
    if (this.mock()) {
      this.showChangeInProgress();
      return;
    }
    this.fireConciergeRequest(subject, {
      kind: 'promotion-setting',
      promotion_key: promo.key,
      platforms: promo.platforms,
      discount_percent: discountPercent,
      threshold_days: nextEdit.thresholdDays
    });
  }

  setPromotionThreshold(promo: PromotionControl, rawValue: string | number) {
    const thresholdDays = this.parseWholeNumber(rawValue, 1);
    if (thresholdDays === null) {
      this.util.showError('Enter a valid number of days.');
      this.cdr.markForCheck();
      return;
    }

    const existing = this.promotionEdits[promo.key] ?? {};
    const discountPercent = existing.discountPercent ?? promo.discountPercent ?? 0;
    const nextEdit: PromotionEdit = {
      ...existing,
      discountPercent,
      thresholdDays
    };
    this.promotionEdits = { ...this.promotionEdits, [promo.key]: nextEdit };
    this.applyPromotionEditToCustomization(promo.key, nextEdit);
    this.cdr.markForCheck();

    const subject = `Set ${promo.name} window to ${thresholdDays} days`;
    if (this.mock()) {
      this.showChangeInProgress();
      return;
    }
    this.fireConciergeRequest(subject, {
      kind: 'promotion-setting',
      promotion_key: promo.key,
      platforms: promo.platforms,
      discount_percent: discountPercent,
      threshold_days: thresholdDays
    });
  }

  private parseWholeNumber(rawValue: string | number, min: number, max?: number): number | null {
    const value = Math.floor(Number(rawValue));
    if (!Number.isFinite(value) || value < min) {return null;}
    if (max !== undefined && value > max) {return null;}
    return value;
  }

  private applyPromotionEditToCustomization(key: PromotionKey, edit: PromotionEdit) {
    if (!this.customization) {return;}
    const discountPercent = edit.discountPercent ?? 0;
    const active = discountPercent > 0;

    if (key === 'weekly-stays') {
      this.customization = { ...this.customization, weeklyDiscountPercent: active ? discountPercent : 0 };
      return;
    }
    if (key === 'monthly-stays') {
      this.customization = { ...this.customization, monthlyDiscountPercent: active ? discountPercent : 0 };
      return;
    }
    if (key === 'last-minute-booking') {
      this.customization = {
        ...this.customization,
        lastMinuteRule: active
          ? { thresholdDays: edit.thresholdDays ?? this.customization.lastMinuteRule?.thresholdDays ?? 7, adjustmentPercent: -discountPercent }
          : undefined
      };
      return;
    }
    if (key === 'far-out-booking') {
      this.customization = {
        ...this.customization,
        farOutRule: active
          ? { thresholdDays: edit.thresholdDays ?? this.customization.farOutRule?.thresholdDays ?? 180, adjustmentPercent: -discountPercent }
          : undefined
      };
    }
  }

  private showChangeInProgress() {
    this.util.showSuccess('Change is in progress. These may take up to two hours to apply.');
  }

  private async fireConciergeRequest(subject: string, metadata: Record<string, any>, bodyOverride?: string) {
    const item: CreateConciergeItemData = {
      type: 'support.other',
      data: {
        subject,
        body: bodyOverride ?? `Please ${subject.toLowerCase()} for this listing.`,
        attachments: [],
        emails: []
      },
      metadata: {
        address_id: this.address()?.id,
        ...metadata
      }
    };
    try {
      await lastValueFrom(this.conciergeApi.createConciergeItem(item));
      this.showChangeInProgress();
    } catch (err: any) {
      this.util.showError(err?.error?.message || 'Failed to start change');
    }
  }

  constructor() {
    effect(() => {
      const addressId = this.address().id;
      const useMock = this.mock();
      this.customization = null;
      this.promotionEdits = {};
      this.cdr.markForCheck();
      if (useMock) {
        this.customization = this.buildMockCustomization();
        this.cdr.markForCheck();
        this.loaded.emit();
        return;
      }
      this.loadData(addressId);
    });
  }

  async loadData(addressId: number) {
    try {
      this.customization = await this.pricingService.getCustomization(addressId);
      this.cdr.markForCheck();
    } catch (err) {
      console.error('Failed to load customization data', err);
    } finally {
      this.loaded.emit();
    }
  }

  formatCents(cents: number | null | undefined): string {
    if (cents === null || cents === undefined) {return '-';}
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  formatDollarsInput(cents: number | null | undefined): string {
    if (cents === null || cents === undefined) {return '';}
    return String(Math.round(cents / 100));
  }

  formatDate(iso: string): string {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  }

  formatOverridePrice(override: PricingOverride): string {
    if (override.price === null || override.price === undefined) {return '—';}
    return this.formatCents(override.price);
  }

  private buildMockCustomization(): PricingCustomization {
    const overrides: PricingOverride[] = [
      { date: '2026-05-14', price: 27000, minPrice: null, maxPrice: null, minStay: 1 },
      { date: '2026-05-28', price: 29000, minPrice: null, maxPrice: null, minStay: 1 }
    ];
    return {
      basePrice: 28000,
      minPrice: 20000,
      maxPrice: 40000,
      overrides,
      source: 'mock',
      recommendedBasePrice: 29500,
      weeklyDiscountPercent: 10,
      monthlyDiscountPercent: 25,
      lastMinuteRule: { thresholdDays: 7, adjustmentPercent: -20 },
      farOutRule: { thresholdDays: 180, adjustmentPercent: -5 }
    };
  }
}
